import mixpanel from "mixpanel-browser";
import { safeStorage } from "@/utils/safeStorage";

const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;

const CAMPAIGN_ID_STORAGE_KEY = "mp_campaign_id";

let initialized = false;

function getCampaignId() {
  const fromUrl = new URLSearchParams(window.location.search).get(
    "campaign-id",
  );
  if (fromUrl) {
    safeStorage.setSessionItem(CAMPAIGN_ID_STORAGE_KEY, fromUrl);
    return fromUrl;
  }

  return safeStorage.getSessionItem(CAMPAIGN_ID_STORAGE_KEY);
}

// Reads campaign-id off the URL as early as possible (app boot, before any
// client-side navigation can drop the query param) so it's already cached in
// sessionStorage by the time a route that fires Mixpanel events mounts.
export function captureCampaignId() {
  getCampaignId();
}

function initMixpanel() {
  if (!token) {
    if (import.meta.env.DEV) {
      console.warn("[Mixpanel] VITE_MIXPANEL_TOKEN is not set");
    }
    return null;
  }

  if (!initialized) {
    mixpanel.init(token, {
      debug: import.meta.env.DEV,
      ignore_dnt: true,
      api_host: "https://api-eu.mixpanel.com",
      // Preview deployments live under a public suffix (e.g. *.vercel.app),
      // so Mixpanel's default cross-subdomain cookie attempt gets rejected
      // by the browser as an invalid cookie domain. Scope to the exact host.
      cross_subdomain_cookie: false,
    });
    mixpanel.register({ app: "consultation-web" });
    initialized = true;
  }

  // Captures campaign-id if present so it rides on this and future events as
  // a super property, but tracking must not be gated on it — most sessions
  // have no campaign-id at all and should still be tracked.
  const campaignId = getCampaignId();
  if (campaignId) {
    mixpanel.register({ campaign_id: campaignId });
  }

  return mixpanel;
}

type MixpanelIdentity = {
  hmoId: string | undefined;
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  providerName: string | undefined;
};

export function identifyMixpanelUser(identity: MixpanelIdentity) {
  try {
    const mp = initMixpanel();
    if (!mp) return;
    const { hmoId, email, firstName, lastName, providerName } = identity;
    mp.identify(hmoId || crypto.randomUUID());
    mp.people.set({
      $name: [firstName, lastName].filter(Boolean).join(" ") || undefined,
      $email: email,
      $first_name: firstName,
      $last_name: lastName,
      partner: providerName || undefined,
      campaign_id: getCampaignId() ?? undefined,
    });
  } catch {
    console.warn("[Mixpanel] Failed to identify user");
  }
}

// Returns whether the event was actually handed to Mixpanel (token was
// present). Callers that persist a "don't retry" dedupe flag should only do
// so when this returns true — otherwise a test run with missing config
// permanently poisons the dedupe key.
export function trackMixpanelEvent(
  name: string,
  data: Record<string, unknown>,
): boolean {
  try {
    const mp = initMixpanel();
    if (!mp) return false;
    mp.track(name, data);
    return true;
  } catch (error) {
    console.warn("[Mixpanel] Failed to track event", error);
    return false;
  }
}
